// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchOrganizationPublicProfile,
  createOrgPublicProfile,
  updateOrgPublicProfile,
} from "../store/publicProfileSlice";
import PublicProfileEditor, { buildPublicProfileInput } from "../components/PublicProfile/PublicProfileEditor";
import { PageContainer, PageHeader } from "../components/AssetBreadCrumb";
import { PageBackButton } from "../components/PageChrome";
import { Skeleton } from "../components/Skeleton";

const ManageOrgPublicProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { organizationId } = useParams();
  const user = useSelector((state) => state.user);
  const orgProfiles = useSelector((state) => state.publicProfile.orgProfiles);

  const { loaded, data } = user.organizations;
  const organizationData = data?.[organizationId];

  // The org owner (organizationData.owner is a User id) or a platform Admin may edit the organization's public profile. Backend `owner` @auth is the real guard on mutations.
  const isPlatformAdmin = user?.details?.groups?.includes("Admin");
  const isOrgOwner = organizationData && organizationData.owner === user?.permissionDetails?.id;
  const canEdit = isPlatformAdmin || isOrgOwner;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (organizationId && canEdit) {
      dispatch(fetchOrganizationPublicProfile(organizationId));
    }
  }, [dispatch, organizationId, canEdit]);

  const handleSave = async (values, toggles) => {
    setSaving(true);
    setSaveError(null);
    try {
      const input = buildPublicProfileInput(organizationId, values, toggles);
      const exists = !!orgProfiles?.[organizationId];
      await dispatch(exists ? updateOrgPublicProfile(input) : createOrgPublicProfile(input)).unwrap();
    } catch (e) {
      console.error("Failed to save organization public profile", e);
      setSaveError("Couldn't save the organization profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <PageContainer>
        <div className="mx-auto w-full max-w-3xl">
          <div className="eai-panel">
            <Skeleton className="h-5 w-48" radius="md" />
            <Skeleton className="mt-2 h-3.5 w-72" />
            <div className="mt-5 flex flex-col gap-4">
              <Skeleton className="h-eai-control-lg w-full" radius="md" />
              <Skeleton className="h-24 w-full" radius="md" />
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!organizationData || !canEdit) {
    return (
      <PageContainer>
        <div className="mx-auto w-full max-w-3xl">
          <PageHeader
            eyebrow="Organization"
            title="Public Profile"
            action={
              <PageBackButton label="Back" onClick={() => navigate(-1)} />
            }
          />
          <div className="eai-panel eai-description-sm">
            You don't have permission to edit this organization's public profile.
          </div>
        </div>
      </PageContainer>
    );
  }

  const orgProfile = orgProfiles?.[organizationId];

  return (
    <PageContainer>
      <div className="mx-auto w-full max-w-3xl">
        <PageHeader
          eyebrow={organizationData.name}
          title="Public Profile"
          description="Control what people see when this organization is credited as the author of a model. Hidden fields are removed from the public record, not just hidden here."
          action={
            <PageBackButton label="Back" onClick={() => navigate(-1)} />
          }
        />

        {!orgProfile && (
          <div className="mb-5 rounded-eai-card border border-eai-border bg-eai-surface-sunken p-4 font-inter text-eai-body-sm text-eai-info-text">
            This organization currently has no public facing profile. Fill in the details below to
            create one.
          </div>
        )}

        <PublicProfileEditor
          title="Organization public profile"
          subtitle="Shown when this organization is credited as the author of a model."
          profile={orgProfile}
          defaults={{
            displayName: organizationData.name || "",
            profilePictureUrl: organizationData.profilePictureUrl || "",
          }}
          saving={saving}
          onSave={handleSave}
          error={saveError}
          id={organizationId}
          uploadFolder={`orgPublicProfile/${organizationId}`}
          lockedFields={["displayName"]}
        />
      </div>
    </PageContainer>
  );
};

export default ManageOrgPublicProfilePage;
